import { SearchForMedicalPaOrdersFromPostgresParams } from "../../../../api/postgreSQL/postCalls/searchForMedicalPaOrdersFromPostgres";
import { DEFAULT_SORT_FOR_SEARCH } from "../../../../constants/defaultSort";
import { FilterValues } from "../../../../data-model/filterValues";
import {
  processFilterData,
  transformDateFields,
  transformObjectToFacetFiltersForPostgres,
} from "../../../../utils/queryConstraint";

/** medical_order_status values that define the "All Open Orders" worklist. */
const ALL_OPEN_ORDER_STATUSES = [
  "order_in_progress",
  "yet_to_start_work_on_order",
];

/**
 * Builds a Postgres-compatible search payload for the All Open Orders worklist.
 *
 * Pins `medical_order_status` to the in-progress statuses so the queue only
 * returns open orders. Unlike My PA Orders, it does not scope by `assigned_to`
 * (all assignees' orders are shown) nor by `master_auth_status`. Any active
 * panel filters (including an optional Date of Creation range) flow through
 * `processFilterData` / `transformDateFields`. `default_sort` is always
 * included as a server-side tiebreaker.
 *
 * @param validFilters - Currently applied filter values from the filter panel
 * @param searchTerm - Free-text search string
 * @param orgId - Organization ID for the query
 * @param sortByField - Optional canonical API field name to sort by
 * @param sortAscending - Optional sort direction; defaults to ascending when sortByField is set
 * @returns Postgres search payload with facet filters, date filters, sort params, and pagination defaults
 */
export const createFilterPayloadForAllOpenOrdersForPostgres = (
  validFilters: FilterValues[],
  searchTerm: string,
  orgId: string,
  sortByField?: string,
  sortAscending?: boolean,
): SearchForMedicalPaOrdersFromPostgresParams => {
  const filterData = processFilterData(validFilters);
  const preComputedFilterData = {
    ...filterData,
    medical_order_status: ALL_OPEN_ORDER_STATUSES,
  };
  const createdFilterQuery = transformObjectToFacetFiltersForPostgres(
    preComputedFilterData,
  );
  const dateFilterQuery = transformDateFields(preComputedFilterData);
  const payload: SearchForMedicalPaOrdersFromPostgresParams = {
    org_id: orgId || "",
    query: searchTerm ?? "",
    facet_filters: createdFilterQuery,
    hits_per_page: 100,
    page: 0,
    ...dateFilterQuery,
    timezone_offset: new Date().getTimezoneOffset(),
    ...(sortByField
      ? {
          sort_by: sortByField,
          sort_dir: sortAscending === false ? "desc" : "asc",
        }
      : {}),
    default_sort: DEFAULT_SORT_FOR_SEARCH,
  };
  return payload;
};
